import axios from 'axios';
import { call, put, select } from 'redux-saga/effects';
import { fallbackRestUrl } from './path';
import { actionObject } from './common';
import { SHOW_LOADING } from '@/store/intermitence/action-types';
import { LOGOUT_USER_ASYNC, REFRESH_TOKEN_ASYNC } from '@/store/auth/action-types';
import { authSelector } from '@/store/selectors';
import router from 'next/router';

const API_URL = process.env.REST_URL || fallbackRestUrl;
const API_URL_SOURCE = process.env.REST_URL ? 'REST_URL' : 'fallbackRestUrl';
const API_RUNTIME = typeof window === 'undefined' ? 'server' : 'browser';
console.log(
  `[Memvy API][${API_RUNTIME}] Using backend: ${API_URL} (source: ${API_URL_SOURCE})`,
);

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;

/**
 * Attempts to refresh the access token using the refresh token
 * Returns the new tokens if successful, null if failed
 */
async function attemptTokenRefresh(refreshToken: string): Promise<{ token: string; refreshToken: string } | null> {
  try {
    const response = await axios.post(`${API_URL}/auth/refresh-token`, {
      refreshToken,
    });
    return {
      token: response.data?.result?.token || response.data?.token,
      refreshToken: response.data?.result?.refreshToken || response.data?.refreshToken,
    };
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
}

function* FetchService(
  url: any,
  method = 'GET',
  variables = {},
  auth: any = null,
  loading: any = true,
): any {
  try {
    if (loading) yield put(actionObject(SHOW_LOADING, true));
    const headers: any = {
      'Content-Type': 'application/json',
    };
    if (auth) headers.Authorization = `Bearer ${auth}`;

    const response = yield call(axios, `${API_URL}/${url}`, {
      headers,
      method,
      data: variables,
    });
    if (loading) yield put(actionObject(SHOW_LOADING, false));
    return response.data;

  } catch (err: any) {
    if (loading) yield put(actionObject(SHOW_LOADING, false));

    if (err?.response?.status === 401) {
      // Don't try to refresh if we're already refreshing or if this IS the refresh request
      if (!isRefreshing && !url.includes('auth/refresh-token')) {
        isRefreshing = true;

        try {
          // Get the current user's refresh token from the store
          const { user } = yield select(authSelector);
          const refreshToken = user?.refreshToken;

          if (refreshToken) {
            // Attempt to refresh the token
            const newTokens: { token: string; refreshToken: string } | null = yield call(
              attemptTokenRefresh,
              refreshToken
            );

            if (newTokens && newTokens.token) {
              // Update the store with new tokens
              yield put(actionObject(REFRESH_TOKEN_ASYNC, newTokens));

              isRefreshing = false;

              // Retry the original request with the new token
              const retryHeaders: any = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${newTokens.token}`,
              };

              if (loading) yield put(actionObject(SHOW_LOADING, true));

              const retryResponse = yield call(axios, `${API_URL}/${url}`, {
                headers: retryHeaders,
                method,
                data: variables,
              });

              if (loading) yield put(actionObject(SHOW_LOADING, false));
              return retryResponse.data;
            }
          }
        } catch (refreshError) {
          console.error('Error during token refresh:', refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // If refresh failed or no refresh token, logout
      if (router) {
        yield put(actionObject(LOGOUT_USER_ASYNC));
        router?.push('/app/register');
        return JSON.stringify(err?.response?.data);
      }
    }

    if (err?.response?.status === 403) {
      if (router) {
        yield put(actionObject(LOGOUT_USER_ASYNC));
        router?.push('/app/home');
      }
    }
    throw new Error(JSON.stringify(err?.response?.data) || '');
  }
}


export default FetchService;
